import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Custom element that displays AND edits the total width (quantity × unit width).
 * Rendered inside the quantity-selector-wrapper alongside the quantity-selector-component.
 *
 * Forward flow: quantity changes → total input value = qty × unit.
 * Reverse flow: user types a total (cm) → qty = ceil(total / unit), committed to
 * the quantity selector, then the total input is reset to qty × unit.
 *
 * @element quantity-total-display
 * @attr {number} data-unit - Width value per unit in cm (e.g. 96 for "96 cm")
 */
class QuantityTotalDisplay extends Component {
  /** @type {AbortController} */
  #abortController = new AbortController();

  /**
   * Quantity to apply after a programmatic width switch settles (via the
   * variant morph). Set by the cheapest-width optimizer, consumed on the next
   * variantUpdate.
   * @type {number|null}
   */
  #pendingQuantity = null;

  connectedCallback() {
    super.connectedCallback();
    this.#attachEventListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#abortController = new AbortController();
  }

  /** @returns {HTMLInputElement|null} */
  get #input() {
    return /** @type {HTMLInputElement|null} */ (
      this.querySelector('.quantity-total-display__input')
    );
  }

  get #unit() {
    return parseFloat(this.dataset.unit ?? '');
  }

  #attachEventListeners() {
    const { signal } = this.#abortController;
    const wrapper = this.closest('.quantity-selector-wrapper');
    const section = this.closest('.shopify-section');

    // React to quantity changes (event bubbles from quantity-selector-component to wrapper)
    wrapper?.addEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#onQuantityUpdate.bind(this),
      { signal }
    );

    // React to variant option changes (e.g. switching Breite value)
    section?.addEventListener(
      ThemeEvents.variantUpdate,
      this.#onVariantUpdate.bind(this),
      { signal }
    );

    // Reverse flow: user edits the total input.
    this.#input?.addEventListener('change', this.#onTotalInput.bind(this), { signal });

    // Prevent Enter from submitting the product form (which would add to cart).
    // Instead, commit the typed total (same as blur/change) and keep focus.
    this.#input?.addEventListener('keydown', this.#onInputKeydown.bind(this), { signal });
  }

  /** @param {KeyboardEvent} event */
  #onInputKeydown(event) {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    this.#onTotalInput();
  }

  /** @param {import('@theme/events').QuantitySelectorUpdateEvent} event */
  #onQuantityUpdate(event) {
    const quantity = event.detail?.quantity;
    if (quantity == null) return;
    this.#render(quantity);
  }

  /** @param {import('@theme/events').VariantUpdateEvent} event */
  #onVariantUpdate(event) {
    // Pull the updated quantity-total-display element from the new HTML document
    // to get the new data-unit value (calculated by Liquid for the new variant)
    const newEl = event.detail?.data?.html?.querySelector('quantity-total-display');

    if (!newEl) {
      // New variant has no width option selected (e.g. non-numeric option) → hide
      this.hidden = true;
      return;
    }

    const newUnit = parseFloat(/** @type {HTMLElement} */ (newEl).dataset.unit ?? '');
    if (!isFinite(newUnit) || newUnit <= 0) {
      this.hidden = true;
      return;
    }

    this.dataset.unit = String(newUnit);
    this.hidden = false;

    // If the optimizer switched the width, apply the pending quantity now that
    // the new variant (and its unit width) is in place.
    if (this.#pendingQuantity != null) {
      const pending = this.#pendingQuantity;
      this.#pendingQuantity = null;
      if (this.#commitQuantity(pending)) return;
    }

    // Re-render with current quantity
    this.#render(this.#currentQuantity());
  }

  /**
   * Reverse flow: user typed a desired total width (cm).
   *
   * When the product exposes several standard (non-range) widths, pick the
   * CHEAPEST width + quantity that covers the requested total, at the current
   * colour (see the cheapest-width optimizer). Otherwise fall back to computing
   * the quantity for the currently selected width.
   */
  #onTotalInput() {
    const unit = this.#unit;
    const input = this.#input;
    if (!input || !isFinite(unit) || unit <= 0) return;

    const total = parseFloat(input.value);

    if (!isFinite(total) || total <= 0) {
      // Invalid/empty → restore the value for the current quantity.
      this.#render(this.#currentQuantity());
      return;
    }

    // When a width RANGE (custom-width) is selected, keep the original behaviour:
    // the entered custom width is the unit, so qty = ceil(total / customWidth).
    // Never auto-switch to a standard width in that mode.
    const best = this.#isCustomWidthActive() ? null : this.#cheapestWidth(total);

    // Switch to a cheaper width: apply its quantity after the variant morph.
    if (best && best.radio && !best.radio.checked) {      this.#pendingQuantity = best.qty;
      best.radio.checked = true;
      best.radio.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    const qty = best ? best.qty : Math.ceil(total / unit);
    if (!this.#commitQuantity(qty)) {
      // No selector found — just normalise the input locally.
      this.#render(qty);
    }
  }

  /**
   * True when a custom-width (range) option is currently selected. In that mode
   * the cheapest-width optimizer is disabled and the entered custom width drives
   * the quantity, preserving the original behaviour.
   * @returns {boolean}
   */
  #isCustomWidthActive() {
    const block = this.closest('.shopify-section')?.querySelector('custom-width-block');
    return block?.getAttribute('data-show') === 'true';
  }

  /**
   * Evaluate every standard numeric width option (rendered with data-width-cm
   * and a current-colour data-price-cents) and return the cheapest one that
   * covers the requested total. Returns null when fewer than two priced widths
   * exist, so single-width products keep the simple ceil() behaviour.
   *
   * @param {number} total - Requested total width in cm.
   * @returns {{ radio: HTMLInputElement, width: number, qty: number, cost: number }|null}
   */
  #cheapestWidth(total) {
    const section = this.closest('.shopify-section');
    const radios = /** @type {HTMLInputElement[]} */ (
      Array.from(section?.querySelectorAll('input[data-width-cm][data-price-cents]') ?? [])
    );

    const candidates = radios
      .filter((radio) => radio.getAttribute('aria-disabled') !== 'true')
      .map((radio) => {
        const width = parseFloat(radio.dataset.widthCm ?? '');
        const price = parseInt(radio.dataset.priceCents ?? '', 10);
        return { radio, width, price };
      })
      .filter((c) => isFinite(c.width) && c.width > 0 && isFinite(c.price) && c.price >= 0);

    if (candidates.length < 2) return null;

    /** @type {{ radio: HTMLInputElement, width: number, qty: number, cost: number }|null} */
    let best = null;
    for (const { radio, width, price } of candidates) {
      const qty = Math.ceil(total / width);
      const cost = qty * price;
      // Cheapest wins; on a tie prefer the larger width (fewer pieces).
      if (best === null || cost < best.cost || (cost === best.cost && width > best.width)) {
        best = { radio, width, qty, cost };
      }
    }
    return best;
  }

  /**
   * Reads the current quantity from the sibling quantity selector.
   * @returns {number}
   */
  #currentQuantity() {
    const qtyInput = /** @type {HTMLInputElement|null} */ (
      this.closest('.quantity-selector-wrapper')?.querySelector('[ref="quantityInput"]')
    );
    return parseFloat(qtyInput?.value ?? '1') || 1;
  }

  /**
   * Commits a quantity to the sibling quantity-selector-component, reusing its
   * own validation/clamping so button states and events stay consistent.
   * @param {number} qty
   * @returns {boolean} true if a selector was found and updated
   */
  #commitQuantity(qty) {
    const wrapper = this.closest('.quantity-selector-wrapper');
    const selector = /** @type {(Element & { setQuantity?: Function }) | null} */ (
      wrapper?.querySelector('[ref="quantitySelector"]') ?? null
    );
    const qtyInput = /** @type {HTMLInputElement|null} */ (
      selector?.querySelector('[ref="quantityInput"]') ?? null
    );
    if (!selector || !qtyInput) return false;

    qtyInput.value = String(qty);

    if (typeof selector.setQuantity === 'function') {
      selector.setQuantity({ target: qtyInput, preventDefault() {} });
    } else {
      qtyInput.dispatchEvent(new Event('blur', { bubbles: true }));
    }
    return true;
  }

  /**
   * @param {number} quantity
   */
  #render(quantity) {
    const unit = this.#unit;
    const input = this.#input;
    if (!input || !isFinite(unit) || unit <= 0) return;
    input.value = String(quantity * unit);
  }
}

if (!customElements.get('quantity-total-display')) {
  customElements.define('quantity-total-display', QuantityTotalDisplay);
}
